import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const normalize = (value) => String(value || '').trim().toLowerCase();

function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Search...',
  disabled = false,
  noResultsText = 'No matches found',
  inputClassName = '',
  menuClassName = '',
  optionClassName = '',
  ariaLabel = '',
  selectedLabelOverride = '',
}) {
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [menuStyle, setMenuStyle] = useState(null);

  const selectedOption = useMemo(
    () => options.find((option) => String(option.value) === String(value)) || null,
    [options, value]
  );
  const selectedLabel = selectedOption && String(selectedOption.value) !== ''
    ? (String(selectedLabelOverride || '').trim() || selectedOption.label)
    : '';

  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalize(query);
    if (!normalizedQuery) {
      return options;
    }
    return options.filter((option) => {
      const searchableText = `${option.label || ''} ${option.description || ''} ${option.searchText || ''}`;
      return normalize(searchableText).includes(normalizedQuery);
    });
  }, [options, query]);

  useEffect(() => {
    if (!isOpen) {
      setQuery(selectedLabel);
    }
  }, [selectedLabel, isOpen]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const updateMenuPosition = useCallback(() => {
    const inputElement = rootRef.current;
    if (!inputElement || typeof window === 'undefined') {
      return;
    }

    const rect = inputElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const safeMargin = 8;
    const gap = 4;

    const availableBelow = Math.max(viewportHeight - rect.bottom - safeMargin, 0);
    const availableAbove = Math.max(rect.top - safeMargin, 0);
    const openUpward = availableBelow < 180 && availableAbove > availableBelow;
    const maxHeight = Math.max(140, Math.min(320, openUpward ? availableAbove : availableBelow));
    const width = Math.min(rect.width, viewportWidth - safeMargin * 2);
    const left = Math.min(Math.max(rect.left, safeMargin), viewportWidth - width - safeMargin);

    setMenuStyle({
      position: 'fixed',
      left: `${left}px`,
      width: `${width}px`,
      maxHeight: `${maxHeight}px`,
      top: openUpward ? 'auto' : `${rect.bottom + gap}px`,
      bottom: openUpward ? `${Math.max(viewportHeight - rect.top + gap, safeMargin)}px` : 'auto',
    });
  }, []);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (rootRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    window.visualViewport?.addEventListener('resize', updateMenuPosition);
    window.visualViewport?.addEventListener('scroll', updateMenuPosition);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.visualViewport?.removeEventListener('resize', updateMenuPosition);
      window.visualViewport?.removeEventListener('scroll', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  const selectOption = (optionValue) => {
    const next = options.find((option) => String(option.value) === String(optionValue)) || null;
    setQuery(next && String(next.value) !== '' ? next.label : '');
    setIsOpen(false);
    if (typeof onChange === 'function') {
      onChange(optionValue);
    }
  };

  const openMenuFromInput = useCallback(() => {
    if (disabled) {
      return;
    }

    if (!isOpen) {
      setIsOpen(true);
    }

    if (normalize(query) === normalize(selectedLabel)) {
      setQuery('');
    }
  }, [disabled, isOpen, query, selectedLabel]);

  return (
    <div ref={rootRef} className="relative">
      <input
        type="text"
        value={query}
        onFocus={() => {
          openMenuFromInput();
        }}
        onPointerDown={openMenuFromInput}
        onClick={() => {
          openMenuFromInput();
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          if (!disabled) {
            setIsOpen(true);
          }
        }}
        onKeyDown={(event) => {
          if (!isOpen && (event.key === 'ArrowDown' || event.key === 'Enter')) {
            event.preventDefault();
            if (!disabled) {
              setQuery('');
              setIsOpen(true);
            }
            return;
          }

          if (!isOpen) {
            return;
          }

          if (event.key === 'Escape') {
            event.preventDefault();
            setIsOpen(false);
            setQuery(selectedLabel);
            return;
          }

          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex((current) => Math.min(current + 1, Math.max(filteredOptions.length - 1, 0)));
            return;
          }

          if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex((current) => Math.max(current - 1, 0));
            return;
          }

          if (event.key === 'Enter') {
            event.preventDefault();
            const option = filteredOptions[highlightedIndex] || null;
            if (option) {
              selectOption(option.value);
            }
          }
        }}
        aria-label={ariaLabel}
        disabled={disabled}
        placeholder={placeholder}
        className={inputClassName}
      />

      {isOpen && !disabled && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          data-searchable-select-menu
          style={menuStyle || undefined}
          className={`z-[220] overflow-auto rounded-md border border-slate-200 bg-white shadow-lg ${menuClassName}`}
          role="listbox"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const isSelected = String(option.value) === String(value);
              const isHighlighted = index === highlightedIndex;
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  onMouseDown={(event) => {
                    // Prevent focus/blur races that can drop option clicks in modal/popover UIs.
                    event.preventDefault();
                  }}
                  onClick={() => {
                    selectOption(option.value);
                  }}
                  className={`block w-full px-3 py-2 text-left text-sm ${
                    isHighlighted ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'
                  } ${isSelected ? 'font-semibold' : ''} ${option.indent ? 'pl-6' : ''} ${optionClassName}`}
                >
                  <span className="block">{option.label}</span>
                  {option.description && (
                    <span className="block text-xs font-normal text-slate-500">{option.description}</span>
                  )}
                </button>
              );
            })
          ) : (
            <div className="px-3 py-2 text-sm text-slate-500">{noResultsText}</div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export default SearchableSelect;
